import { FC } from 'react'
import { Box, IconButton, Typography, Skeleton } from '@mui/material'
import theme from 'theme'

/* Utils */
import { SSMAssetType, SSMNistPillar, SSMResponseProduct } from 'models'
import { getIntegrationLogoSSM } from 'lib/utils'

/* Components */
import {
  IntegrationLogo,
  IntegrationItemWrapper,
  NoIntegrationLogo,
  SSMCardContainer,
  SSMCardInner,
  SSMCardSkeletonWrapper,
  SSMCardSkeleton,
  ProductCount,
  IconsRow,
  IconWrapper,
  OverflowIndicator,
  HoverTooltipContent
} from './ssmCard.styles'
import { SSMEditCellIcon } from '../../../components/svg'
import { PermissionTooltip } from '../../../components/tooltip'
import { ChipTooltip } from '../../../components/tooltip/ChipTooltip'

const MAX_VISIBLE_ICONS = 10

interface Props {
  nist: SSMNistPillar
  asset: SSMAssetType
  items: SSMResponseProduct[]
  handleOpenDrawer: (nist: SSMNistPillar, asset: SSMAssetType, assets: SSMResponseProduct[]) => void
  handleCloseDrawer: () => void
  loading: boolean
  canEdit?: boolean
}

const SSMCard: FC<Props> = ({ nist, asset, items, loading, canEdit, handleOpenDrawer }) => {
  const sortedItems = [...items].sort((a, b) =>
    (a?.display_name || '').localeCompare(b?.display_name || '', undefined, { sensitivity: 'base' })
  )
  const visibleItems = sortedItems.slice(0, MAX_VISIBLE_ICONS)
  const overflowCount = sortedItems.length - MAX_VISIBLE_ICONS

  const renderIcon = (item: SSMResponseProduct) => {
    const logoPath = getIntegrationLogoSSM(item.logo_path)
    if (
      item.logo_path &&
      item.logo_path.length > 0 &&
      !logoPath.includes('default-vendor-logo') &&
      !logoPath.includes('N/A')
    ) {
      return <IntegrationLogo logo={logoPath} />
    }
    return (
      <NoIntegrationLogo>
        <Typography className="no-integration-logo-letter">
          {item.vendor?.display_name?.[0] || item.display_name?.[0] || ''}
        </Typography>
      </NoIntegrationLogo>
    )
  }

  const tooltipContent = (
    <HoverTooltipContent>
      {sortedItems.map((item, idx) => {
        const logoPath = getIntegrationLogoSSM(item.logo_path)
        const hasLogo =
          item.logo_path &&
          item.logo_path.length > 0 &&
          !logoPath.includes('default-vendor-logo') &&
          !logoPath.includes('N/A')
        return (
          <Box key={idx} className="hover-tooltip-item">
            {hasLogo ? (
              <Box className="hover-tooltip-logo" sx={{ backgroundImage: `url("${logoPath}")` }} />
            ) : (
              <Box className="hover-tooltip-missing-logo">
                {item.vendor?.display_name?.[0] || item.display_name?.[0] || ''}
              </Box>
            )}
            <Box className="hover-tooltip-text">
              <Typography className="hover-tooltip-product-name">{item.display_name}</Typography>
              {item.is_product && item.vendor && (
                <Typography className="hover-tooltip-vendor-name">{item.vendor.display_name}</Typography>
              )}
            </Box>
          </Box>
        )
      })}
    </HoverTooltipContent>
  )

  return (
    <SSMCardContainer
      className={`ssm-card-container ${items.length > 0 && 'with-items'}`}
      isloading={loading ? 'true' : 'false'}
    >
      {loading ? (
        <SSMCardSkeletonWrapper>
          <SSMCardSkeleton>
            <Skeleton
              variant="rectangular"
              component="div"
              animation="wave"
              width="100%"
              height="100%"
              className="card-skeleton-inner"
            />
          </SSMCardSkeleton>
          <SSMCardSkeleton>
            <Skeleton
              variant="rectangular"
              component="div"
              animation="wave"
              width="100%"
              height="100%"
              className="card-skeleton-inner"
            />
          </SSMCardSkeleton>
        </SSMCardSkeletonWrapper>
      ) : (
        <>
          {canEdit ? (
            <IconButton className="edit-icon-button" onClick={() => handleOpenDrawer(nist, asset, items)}>
              <SSMEditCellIcon />
            </IconButton>
          ) : (
            <PermissionTooltip
              PopperProps={{
                sx: {
                  marginTop: `-${theme.spacing.sm}px !important`
                }
              }}
              title={
                <>
                  <Box className="tooltip-wrapper-permissions">
                    <Typography className="tooltip-text">Insufficient permissions</Typography>
                  </Box>
                </>
              }
            >
              <IconButton className="edit-icon-button" disableRipple disableTouchRipple disableFocusRipple>
                <SSMEditCellIcon />
              </IconButton>
            </PermissionTooltip>
          )}

          <SSMCardInner className="ssm-card-inner">
            {items.length > 0 && (
              <>
                <ProductCount>
                  {items.length} {items.length === 1 ? 'Product' : 'Products'}
                </ProductCount>
                <ChipTooltip
                  placement="right"
                  title={tooltipContent}
                  disableInteractive={false}
                  leaveDelay={150}
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 12]
                        }
                      }
                    ],
                    sx: {
                      [`& .MuiTooltip-tooltip`]: {
                        maxWidth: '240px'
                      }
                    }
                  }}
                >
                  <IconsRow>
                    {visibleItems.map((item, idx) => {
                      if (item && item.display_name) {
                        return (
                          <IconWrapper key={idx}>
                            <IntegrationItemWrapper>{renderIcon(item)}</IntegrationItemWrapper>
                          </IconWrapper>
                        )
                      }
                      return null
                    })}
                    {overflowCount > 0 && <OverflowIndicator>+{overflowCount}</OverflowIndicator>}
                  </IconsRow>
                </ChipTooltip>
              </>
            )}
          </SSMCardInner>
        </>
      )}
    </SSMCardContainer>
  )
}

export default SSMCard
